/**
 * Renderer registry — React-first card rendering for AI tool calls.
 *
 * Today, hooking up a new card-rendering tool requires touching multiple
 * places (server tool def, choreography emit, dispatcher case, push fn,
 * render fn, CSS). The registry collapses the FRONTEND side of that
 * into a single self-contained module per renderer.
 *
 * Each renderer module:
 *   1. Imports `registerRenderer` from this file
 *   2. Defines a React component that renders one card of that kind
 *   3. Calls `registerRenderer({ tool: "render_X", Component: ... })`
 *
 * Surfaces that paint AI work done over MCP (the live canvas and the
 * dashboard feed) consult the registry by tool name. Any tool with a
 * registered renderer paints a card; tools without one fall back to the
 * action trace.
 */

import type { ComponentType } from "react";

/**
 * Props every renderer component receives. `input` is the choreography
 * payload (shape determined by the renderer); `consumed` flips when the
 * card has been acted on (plan approved, diff undone) so renderers can
 * grey out their action buttons; `onConsume` is the callback the
 * renderer fires to mark itself consumed AND request a follow-up
 * (typically: send a message back to the AI to trigger a reverse
 * operation).
 */
export interface RendererProps<TInput = unknown> {
  input: TInput;
  consumed?: boolean;
  onConsume?: (action: ConsumeAction) => void;
}

export interface ConsumeAction {
  /** Free-form kind tag, e.g. "undo" / "approve" / "cancel". */
  kind: string;
  /** Optional payload — passed back to the host so it can construct a
   *  matching follow-up message to the AI. */
  payload?: unknown;
  /** Human prompt the host should send to the AI to trigger the
   *  follow-up (e.g. "undo: <title>. The reversible_action was {...}"). */
  followupMessage?: string;
}

export interface RendererSpec<TInput = unknown> {
  /** Tool name this renderer handles, e.g. "render_diff_panel". */
  tool: string;
  /** Optional human label for dev/debug surfaces. */
  label?: string;
  /** React component that renders one card for this tool. */
  Component: ComponentType<RendererProps<TInput>>;
}

export class RendererRegistry {
  private map = new Map<string, RendererSpec<unknown>>();

  register<TInput>(spec: RendererSpec<TInput>): void {
    if (this.map.has(spec.tool)) {
       
      console.warn(`[RendererRegistry] Overwriting renderer for "${spec.tool}"`);
    }
    this.map.set(spec.tool, spec as RendererSpec<unknown>);
  }

  get(tool: string): RendererSpec<unknown> | undefined {
    return this.map.get(tool);
  }

  has(tool: string): boolean {
    return this.map.has(tool);
  }

  list(): RendererSpec<unknown>[] {
    return Array.from(this.map.values());
  }
}

/**
 * Default singleton — most apps only need one. Host apps that want
 * isolated registries (multi-tenant rendering, testing) construct
 * `new RendererRegistry()` directly.
 */
export const defaultRegistry = new RendererRegistry();

/** Convenience binding to the default singleton. */
export function registerRenderer<TInput>(spec: RendererSpec<TInput>): void {
  defaultRegistry.register(spec);
}
